import { ImageWithFallback } from "./figma/ImageWithFallback";
import { Shield, CheckCircle, Award } from "lucide-react";
import ieslogo from "../assets/IES-Logo.jpg";
import { motion } from "motion/react";
import { SwitchToGreenEnergyForm } from "./SwitchToGreenEnergy";

export function Hero() {
  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-emerald-50 via-blue-50 to-cyan-50">
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSgxNiwgMTg1LCAxMjksIDAuMDUpIiBzdHJva2Utd2lkdGg9IjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-50"></div>

      <div className="container mx-auto px-4 py-16 md:py-24 lg:py-32 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="space-y-8"
          >
            <div className="inline-block">
              <span className="px-4 py-2 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white rounded-full text-sm">
                24/7 Carbon Free Energy
              </span>
            </div>

            <h1 className="translate-y-[100px] text-4xl md:text-5xl lg:text-6xl bg-gradient-to-r from-emerald-600 to-cyan-600 bg-clip-text text-transparent"
             style={{paddingBottom: "12px"}}
            >
              Switch to Green Energy. Take Control of the Color of Your Energy.
            </h1>

            <p className="text-lg md:text-xl text-gray-600 max-w-xl">
              Access verified renewable energy with our Green Tariff program. Get hourly-matched Green Energy Certificates (GEC) that prove your commitment to 24/7 carbon-free energy consumption.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <a
                // href={`${import.meta.env.VITE_API_URL}/auth/login`}
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-4 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white rounded-lg hover:shadow-xl transition-shadow text-center"
              >
                Opt-in to Green Tariff
              </a>
              <button className="px-8 py-4 border-2 border-emerald-500 text-emerald-600 rounded-lg hover:bg-emerald-50 transition-colors">
                Track My Green Energy
              </button>
            </div>

            {/* Badges */}
            
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 pt-8">
                     <div className="flex items-center gap-3 p-4 bg-white rounded-lg shadow-sm">
                <img src={ieslogo} alt="IES Logo" className="w-8 h-8 object-contain" />
                <div>
                  <p className="text-sm text-gray-500">IES</p>
                  <p className="text-sm">Enabled</p>
                </div>
              </div>
                   <div className="flex items-center gap-3 p-4 bg-white rounded-lg shadow-sm">
                <Award className="w-8 h-8 text-emerald-500" />
                <div>
                  <p className="text-sm text-gray-500">DISCOM</p>
                  <p className="text-sm">Verified</p>
                </div>
              </div>
         
              

              <div className="flex items-center gap-3 p-4 bg-white rounded-lg shadow-sm">
                <CheckCircle className="w-8 h-8 text-cyan-500" />
                <div>
                  <p className="text-sm text-gray-500">Tamper-proof</p>
                  <p className="text-sm">Hourly Match</p>
                </div>
              </div>

                   <div className="flex items-center gap-3 p-4 bg-white rounded-lg shadow-sm">
                <Shield className="w-8 h-8 text-emerald-500" />
                
                <div>
                  <p className="text-sm text-gray-500">Global</p>
                  <p className="text-sm">Verification</p>
                </div>
              </div>

         

       
            </div>
          </motion.div>

          {/* Right side — form overlays energy images */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            {/* Energy image grid — absolute faded background on desktop, normal flow on mobile */}
            <div className="hero-images-bg">
              <div className="relative rounded-2xl overflow-hidden shadow-xl group">
                <ImageWithFallback
                  src="https://images.unsplash.com/photo-1508514177221-188b1cf16e9d?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxzb2xhciUyMHBhbmVsJTIwZmFybXxlbnwxfHx8fDE3NjQ0MTM4MDR8MA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral"
                  alt="Solar Energy"
                  className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-yellow-600/60 to-transparent"></div>
                <div className="absolute bottom-3 left-3 text-white">
                  <p className="text-sm">Solar Power</p>
                </div>
              </div>
              <div className="relative rounded-2xl overflow-hidden shadow-xl group">
                <ImageWithFallback
                  src="https://images.unsplash.com/photo-1566933971571-e3cb6990e9f8?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHx3aW5kJTIwdHVyYmluZSUyMGZpZWxkfGVufDF8fHx8MTc2NDMyMTQxMXww&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral"
                  alt="Wind Energy"
                  className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-cyan-600/60 to-transparent"></div>
                <div className="absolute bottom-3 left-3 text-white">
                  <p className="text-sm">Wind Power</p>
                </div>
              </div>
              <div className="relative rounded-2xl overflow-hidden shadow-xl group">
                <ImageWithFallback
                  src="https://images.unsplash.com/photo-1733001523587-ad1bc6d025c1?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxoeWRyb2VsZWN0cmljJTIwZGFtJTIwcG93ZXJ8ZW58MXx8fHwxNzY0NDEzODA0fDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral"
                  alt="Hydro Energy"
                  className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-blue-600/60 to-transparent"></div>
                <div className="absolute bottom-3 left-3 text-white">
                  <p className="text-sm">Hydro Power</p>
                </div>
              </div>
              <div className="relative rounded-2xl overflow-hidden shadow-xl group">
                <ImageWithFallback
                  src="https://images.unsplash.com/photo-1747345341772-7516f6b54958?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxiaW9tYXNzJTIwZW5lcmd5JTIwcGxhbnR8ZW58MXx8fHwxNzY0NDEzODA1fDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral"
                  alt="Biomass Energy"
                  className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-emerald-600/60 to-transparent"></div>
                <div className="absolute bottom-3 left-3 text-white">
                  <p className="text-sm">Biomass Power</p>
                </div>
              </div>
            </div>

            {/* Form — always on top with z-10 */}
            <div className="relative z-10">
              <SwitchToGreenEnergyForm />
            </div>

            {/* Floating Certificate Badge */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
              className="absolute -bottom-6 -right-6 bg-white p-6 rounded-xl shadow-xl border border-emerald-100 hidden lg:block"
              style={{ zIndex: 20 }}
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-emerald-400 to-cyan-400 rounded-lg flex items-center justify-center">
                  <Award className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Digital</p>
                  <p>GEC Certificate</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
